
  import Combobox from "@github/combobox-nav"

  const MAX_MENTIONS = 8

  export default {
    mounted() {
      this.clientIdInput = this.el.closest("form").querySelector('input[name="message[client_id]"]')
      this.mentionPicksInput = this.el.form.querySelector('input[name="message[mention_picks]"]')
      this.bindMentionList()
      this.mentionPicks = []
      this.mentionCompletions = []
      this.mintClientId()

      this.el.form.addEventListener("input", (event) => {
        if (event.target === this.el) this.refreshMentions()
      })

      this.el.addEventListener("blur", () => setTimeout(() => {
        const inside = document.activeElement === this.el || this.mentionList.contains(document.activeElement)
        if (!inside) this.closeMentions()
      }, 120))

      // A screenshot reaches a tester's clipboard far more often than their filesystem, so
      // pasting one has to be the same as picking it. `upload` hands the files to the same
      // entries the file input feeds.
      this.el.addEventListener("paste", (event) => {
        const files = Array.from(event.clipboardData?.files || [])
        if (!files.length) return
        event.preventDefault()
        this.attach(files)
      })

      this.dropZone = this.el.form.closest("[data-chat-drop]") || this.el.form

      this.onDragOver = (event) => {
        if (!event.dataTransfer?.types.includes("Files")) return
        event.preventDefault()
        this.dropZone.setAttribute("data-dropping", "true")
      }

      this.onDragLeave = (event) => {
        if (!this.dropZone.contains(event.relatedTarget)) this.dropZone.removeAttribute("data-dropping")
      }

      this.onDrop = (event) => {
        if (!event.dataTransfer?.types.includes("Files")) return
        event.preventDefault()
        this.dropZone.removeAttribute("data-dropping")
        const files = Array.from(event.dataTransfer?.files || [])
        if (files.length) this.attach(files)
      }

      this.dropZone.addEventListener("dragover", this.onDragOver)
      this.dropZone.addEventListener("dragleave", this.onDragLeave)
      this.dropZone.addEventListener("drop", this.onDrop)

      // mousedown, not click: preventing the default keeps the caret in the textarea, so the
      // emoji lands where the tester was typing rather than at the start.
      this.el.form.addEventListener("mousedown", (event) => {
        const choice = event.target.closest("[data-emoji]")
        if (!choice) return
        event.preventDefault()
        this.insertEmoji(choice.dataset.emoji)
      })

      this.el.addEventListener("keydown", (event) => {
        if (event.isComposing) return
        if (!this.mentionList.hidden) {
          if (event.key === "Escape") {
            event.preventDefault()
            event.stopPropagation()
            this.closeMentions()
          }
          if (event.key === "Enter" || event.key === "Escape") return
        }
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault()
          if (this.hasContent() && !this.el.form.querySelector('button[type="submit"]').disabled) this.el.form.requestSubmit()
        }
      })

      this.handleEvent("chat:sent", ({content}) => {
        if (this.el.value.trim() === content) this.clearBox()
        else this.dropMissingMentions()
        requestAnimationFrame(() => this.el.focus())
        this.mintClientId()
      })

      this.handleEvent("chat:already_sent", () => this.mintClientId())
    },

    updated() {
      this.bindMentionList()
    },

    destroyed() {
      this.dropZone.removeEventListener("dragover", this.onDragOver)
      this.dropZone.removeEventListener("dragleave", this.onDragLeave)
      this.dropZone.removeEventListener("drop", this.onDrop)
    },

    reconnected() {
      const stored = document.querySelector(`#chat-messages [data-client-id="${this.clientIdInput.value}"]`)
      if (!stored) return
      const text = stored.querySelector("[data-message-content]")?.innerText.trim() ?? ""
      if (text === this.el.value.trim()) this.clearBox()
      this.mintClientId()
    },

    bindMentionList() {
      const list = document.getElementById(this.el.dataset.mentionList)
      if (list === this.mentionList) return

      this.mentionCombobox?.stop()
      this.mentionList = list
      this.mentionCombobox = new Combobox(this.el, list, {firstOptionSelectionMode: "active"})
      list.addEventListener("combobox-commit", (event) => this.pickMention(event.target))
      list.addEventListener("mousedown", (event) => event.preventDefault())
    },

    clearBox() {
      this.el.value = ""
      this.mentionCompletions = []
      this.mentionPicks = []
      this.writeMentionPicks()
      this.closeMentions()
    },

    // The token being typed is whatever follows the last "@" before the caret, as long as it
    // has not run past a line break.
    mentionQuery() {
      const upToCaret = this.el.value.slice(0, this.el.selectionStart)
      const at = upToCaret.lastIndexOf("@")
      if (at === -1 || at < this.mentionFloor()) return null
      if (at > 0 && !/[\s(\["']/.test(upToCaret[at - 1])) return null

      const token = upToCaret.slice(at + 1)
      return token.includes("\n") ? null : {at, token}
    },

    mentionFloor() {
      return Math.max(0, ...this.mentionCompletions.map((completed) => {
        const at = this.el.value.lastIndexOf(completed)
        return at === -1 ? 0 : at + completed.length
      }))
    },

    refreshMentions() {
      this.mentionCompletions = this.mentionCompletions.filter((completed) => this.el.value.includes(completed))
      const query = this.mentionQuery()
      let shown = 0

      for (const option of this.mentionList.querySelectorAll('[role="option"]')) {
        const match = query !== null && shown < MAX_MENTIONS &&
          option.dataset.mentionName.toLowerCase().startsWith(query.token.toLowerCase())
        option.hidden = !match
        if (match) shown++
      }

      this.mentionCombobox.clearSelection()
      if (shown === 0) return this.closeMentions()
      this.mentionList.hidden = false
      this.mentionCombobox.start()
    },

    pickMention(option) {
      const query = this.mentionQuery()
      if (!query) return this.closeMentions()

      const completed = `@${option.dataset.mentionName} `
      this.el.value = this.el.value.slice(0, query.at) + completed + this.el.value.slice(this.el.selectionStart)
      const position = query.at + completed.length
      this.el.setSelectionRange(position, position)
      this.mentionCompletions.push(completed)
      if (option.dataset.mentionId) {
        this.mentionPicks.push({id: option.dataset.mentionId, name: option.dataset.mentionName})
        this.writeMentionPicks()
      }
      this.closeMentions()
      this.el.focus()
    },

    closeMentions() {
      if (this.mentionList.hidden) return
      this.mentionList.hidden = true
      this.mentionCombobox.stop()
    },

    dropMissingMentions() {
      this.mentionPicks = this.mentionPicks.filter(({name}) => this.el.value.includes(`@${name}`))
      this.writeMentionPicks()
    },

    writeMentionPicks() {
      this.mentionPicksInput.value = JSON.stringify(this.mentionPicks)
    },

    hasContent() {
      return this.el.value.trim() !== "" || document.querySelector(`#${this.el.dataset.uploadId} li`) !== null
    },

    attach(files) {
      const input = document.querySelector(`#${this.el.dataset.uploadId} input[type=file]`)
      if (input) this.upload(input.name, files)
    },

    mintClientId() {
      this.clientIdInput.value = crypto.randomUUID()
    },

    insertEmoji(emoji) {
      const start = this.el.selectionStart
      const end = this.el.selectionEnd
      this.el.value = this.el.value.slice(0, start) + emoji + this.el.value.slice(end)
      const position = start + emoji.length
      this.el.setSelectionRange(position, position)
      this.el.focus()
    }
  }
